/**
 * Cluster 2 — Screen E : PPA Reconciliation.
 *
 * Layout:
 *   - Tab strip
 *   - Period selector
 *   - KPI strip (Settlement / Min Gen vs target / Avg Strike vs Spot / Compliance %)
 *   - Per-PPA delivered vs contracted — table with bar inline
 *   - Settlement composition — progress-bar list (PPA fixed / Spot merchant / Cap / FCAS / LGC)
 *   - Shortfall attribution — bucket list (Network / P50 pending / Other)
 *   - Settlement reconciliation per counterparty — table
 *
 * Data: GET /api/portfolios/<id>/ppa-reconcile?period=...
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, Chip,
  Tooltip, LinearProgress,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { BarChart } from '@mui/x-charts/BarChart';
import axios from 'axios';

import KpiCard, { fmtAUDCompact, fmtMWhCompact, fmtAUDPerMWh } from './KpiCard';
import { periodApiParams } from '../utils/periodParams';
import { useThemeColors } from '../theme/ThemeModeContext';
import PpaWorkspaceTabs from './PpaWorkspaceTabs';
import PeriodPicker, { defaultPeriodValue } from './PeriodPicker';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const STATUS_TINT = {
  on_target:         { color: '#22c55e', label: 'On target' },
  near_target:       { color: '#84cc16', label: 'Near target' },
  shortfall:         { color: '#f59e0b', label: 'Shortfall' },
  severe_shortfall:  { color: '#ef4444', label: 'Severe' },
};

const COMPONENT_COLOR = {
  'PPA fixed':     '#D98A2B',
  'Spot merchant': '#E9B065',
  'Cap product':   '#3b82f6',
  'FCAS':          '#f59e0b',
  'LGC value':     '#22c55e',
};

const OFFTAKE_LABEL = {
  pay_as_produced: 'PAYG',
  baseload:        'Baseload',
  shaped:          'Shaped',
  sleeved:         'Sleeved',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}


// ── Per-PPA row (delivered-vs-contracted) ────────────────────────────────

function PerPpaRow({ p, colors }) {
  const tint = STATUS_TINT[p.status] || STATUS_TINT.shortfall;
  // Pay-as-produced PPAs have no contractual volume target — show "n/a",
  // never a fabricated %/shortfall.
  const hasTarget = p.delivered_pct != null && p.contracted_mwh != null;
  const fillPct = hasTarget ? Math.min(p.delivered_pct, 100) : 0;

  return (
    <Box sx={{
      px: 2, py: 1.5,
      borderBottom: '1px solid', borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
    }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
              {p.counterparty}
            </Typography>
            <Chip
              label={tint.label}
              size="small"
              sx={{
                bgcolor: `${tint.color}20`, color: tint.color,
                height: 18, fontSize: '0.65rem', fontWeight: 700,
                letterSpacing: '0.06em', borderRadius: '4px',
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {p.asset}
            </Typography>
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {OFFTAKE_LABEL[p.offtake] || p.offtake || '—'}
            </Typography>
          </Stack>
        </Box>
        <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Typography sx={{
            fontWeight: 700, fontSize: '1rem',
            color: hasTarget ? tint.color : 'text.disabled',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {hasTarget ? `${p.delivered_pct.toFixed(1)}%` : 'n/a'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {hasTarget ? 'of target' : 'no volume target'}
          </Typography>
        </Stack>
      </Stack>

      {/* Delivered vs contracted bar */}
      <Box sx={{
        height: 6, borderRadius: 3, bgcolor: colors.track,
        position: 'relative', overflow: 'hidden', mb: 0.75,
      }}>
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${fillPct}%`,
          bgcolor: tint.color, opacity: 0.85,
          transition: 'width 240ms ease',
        }} />
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center"
             sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
        <Box>
          Delivered <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {fmtMWhCompact(p.delivered_mwh)}
          </Box>
          <Box component="span" sx={{ mx: 0.75 }}>/</Box>
          Contracted <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {p.contracted_mwh == null ? 'n/a' : fmtMWhCompact(p.contracted_mwh)}
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          {p.shortfall_mwh > 0 && (
            <span>Shortfall: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{fmtMWhCompact(p.shortfall_mwh)}</Box></span>
          )}
          {p.ld_aud > 0 && (
            <Tooltip title={`Liquidated damages @ ${p.ld_rate ? 'A$'+p.ld_rate.toFixed(0) : '—'}/MWh shortfall`} arrow>
              <span>LD: <Box component="span" sx={{ color: '#ef4444', fontWeight: 600 }}>{fmtAUDCompact(p.ld_aud)}</Box></span>
            </Tooltip>
          )}
          <span>Settlement: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{fmtAUDCompact(p.settlement_aud)}</Box></span>
        </Stack>
      </Stack>

      {/* Expected vs actual revenue ($) — only when the PPA has a computable
          budget (MEG × share × strike). */}
      {p.expected_revenue_aud != null && (
        <Stack direction="row" justifyContent="space-between" alignItems="center"
               sx={{ mt: 0.5, fontSize: '0.78rem', color: 'text.secondary' }}>
          <Box>
            Revenue — Expected{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmtAUDCompact(p.expected_revenue_aud)}
            </Box>
            <Box component="span" sx={{ mx: 0.75 }}>/</Box>
            Actual{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmtAUDCompact(p.actual_revenue_aud)}
            </Box>
          </Box>
          {p.revenue_variance_aud != null && (
            <Box component="span" sx={{
              fontWeight: 700,
              color: p.revenue_variance_aud >= 0 ? '#22c55e' : '#ef4444',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {p.revenue_variance_aud >= 0 ? '+' : '−'}{fmtAUDCompact(Math.abs(p.revenue_variance_aud))}
              {p.revenue_variance_pct != null && ` (${p.revenue_variance_aud >= 0 ? '+' : ''}${p.revenue_variance_pct.toFixed(1)}%)`}
            </Box>
          )}
        </Stack>
      )}

      {/* Neg-price min-gen carve-out audit line — surfaces only when the
          contract opts into the relief, so a vanilla PPA doesn't get an
          extra row. The hour count is the figure subtracted from the
          contracted-MWh denominator. */}
      {p.neg_price_excluded && p.neg_price_hours_excluded != null && (
        <Tooltip
          title={
            `Min-gen relief: ${p.neg_price_hours_excluded.toFixed(1)} h with RRP ≤ `
            + `A$${(p.neg_price_threshold ?? 0).toFixed(0)}/MWh excluded — `
            + `${(p.effective_hours ?? 0).toFixed(1)} h applied to the target.`
          }
          arrow
        >
          <Stack direction="row" spacing={0.5} alignItems="center"
                 sx={{ mt: 0.5, fontSize: '0.7rem', color: 'text.disabled' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                       bgcolor: '#94a3b8' }} />
            <Box>
              Excludes&nbsp;
              <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {p.neg_price_hours_excluded.toFixed(1)} h
              </Box>
              &nbsp;of neg-priced intervals from the min-gen target
            </Box>
          </Stack>
        </Tooltip>
      )}
    </Box>
  );
}


// ── Composition row ──────────────────────────────────────────────────────

function CompositionRow({ row, colors }) {
  const color = COMPONENT_COLOR[row.component] || colors.textMute;
  return (
    <Box sx={{ py: 0.75 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <FiberManualRecordIcon sx={{ fontSize: 9, color }} />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {row.component}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
            {fmtAUDCompact(row.aud)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 42, textAlign: 'right' }}>
            {row.pct.toFixed(1)}%
          </Typography>
        </Stack>
      </Stack>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: colors.track, overflow: 'hidden' }}>
        <Box sx={{
          height: '100%', width: `${Math.min(Math.abs(row.pct), 100)}%`,
          bgcolor: color, opacity: 0.85, transition: 'width 240ms ease',
        }} />
      </Box>
    </Box>
  );
}


// ── Shortfall bucket row ─────────────────────────────────────────────────

function ShortfallBucket({ bucket, total, colors }) {
  const pending = bucket.mwh == null;
  const pct = pending || !total ? 0 : (bucket.mwh / total) * 100;
  const color = pending ? colors.textMute : (
    bucket.cause.includes('Network') ? '#3b82f6' :
    bucket.cause.includes('Other')   ? '#f59e0b' :
    '#E9B065'
  );

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.4 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <FiberManualRecordIcon sx={{ fontSize: 9, color }} />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {bucket.cause}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          <Typography sx={{
            fontWeight: 600, fontSize: '0.88rem', fontVariantNumeric: 'tabular-nums',
            color: pending ? 'text.disabled' : 'text.primary',
            fontStyle: pending ? 'italic' : 'normal',
          }}>
            {pending ? 'Pending' : fmtMWhCompact(bucket.mwh)}
          </Typography>
          {!pending && (
            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 42, textAlign: 'right' }}>
              {pct.toFixed(0)}%
            </Typography>
          )}
        </Stack>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'block', ml: 2 }}>
        {bucket.source}
      </Typography>
      {!pending && (
        <Box sx={{ height: 4, borderRadius: 2, bgcolor: colors.track, overflow: 'hidden', mt: 0.5 }}>
          <Box sx={{
            height: '100%', width: `${Math.min(pct, 100)}%`,
            bgcolor: color, opacity: 0.85,
          }} />
        </Box>
      )}
    </Box>
  );
}


// ── Clause 4.7 — Short-fall Electricity ──────────────────────────────────
//
// Contract-Year volume vs target:
//   ESQ = (Buyer's Share × MEG × LF) − [ACQ + curtailment add-backs × Share × LF]
// The meter stacks what counts toward the target (delivered ACQ + economic +
// non-economic curtailment add-backs); the haloed tick marks the pro-rata
// position the volume "should" be at, given how far through the Contract
// Year we are.

const CLAUSE47_SEGMENTS = [
  { key: 'acq_mwh',                       label: 'Contract Quantity (delivered)', color: '#D98A2B' },
  { key: 'econ_curtailment_addback_mwh',  label: 'Economic curtailment add-back', color: '#E9B065' },
  { key: 'grid_curtailment_addback_mwh',  label: 'Non-economic (network) add-back', color: '#3b82f6' },
];

function Clause47Card({ contract, colors }) {
  const s = contract.shortfall_4_7 || {};
  const t = s.terms || {};
  const cy = s.current_year;
  const completed = s.completed_years || [];

  if (!cy && !completed.length) return null;

  const target = cy?.target_mwh || 0;
  const tickPct = cy && target > 0 ? Math.min((cy.pro_rata_target_mwh / target) * 100, 100) : null;
  const ahead = cy?.tracking_delta_mwh != null && cy.tracking_delta_mwh >= 0;
  const projShortfall = (cy?.projected_esq_mwh || 0) > 0;

  const formulaTip =
    'Clause 4.7(a): ESQ = (Buyer’s Share × Minimum Expected Generation × LF) − '
    + '[Annual Contract Quantity + (Economic Curtailment × Share × LF) + '
    + '(Non-Economic Curtailment × Share × LF)]. Negative → deemed zero (4.7(b)). '
    + `Payment = max(0, ESQ × (QLD Average Spot Price − Fixed Price × ${(t.fixed_price_floor_pct ?? 65).toFixed(0)}%)) (4.7(c)).`;

  return (
    <Card sx={{ mb: 2, p: 0 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between"
             sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tooltip title={formulaTip} arrow>
          <Typography variant="overline" sx={{ color: 'text.secondary', cursor: 'help' }}>
            Short-fall Electricity · Clause 4.7
          </Typography>
        </Tooltip>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {cy ? `${cy.label} · ${fmtDate(cy.start)} → ${fmtDate(cy.end)} · ${cy.elapsed_pct.toFixed(0)}% elapsed` : 'No Contract Year in progress'}
        </Typography>
      </Stack>

      {cy && (
        <Box sx={{ px: 2.25, py: 2 }}>
          {/* Volume vs Target meter */}
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Volume vs target — {contract.asset}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', fontVariantNumeric: 'tabular-nums',
                              color: ahead ? '#22c55e' : '#f59e0b' }}>
              {fmtMWhCompact(cy.adjusted_volume_mwh)}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '0.85rem' }}>
                {' '}/ {fmtMWhCompact(target)} target
              </Box>
            </Typography>
          </Stack>

          <Box sx={{ position: 'relative', height: 14, my: 1 }}>
            <Box sx={{ position: 'absolute', inset: '2px 0', borderRadius: 5,
                       bgcolor: colors.track, overflow: 'hidden', display: 'flex' }}>
              {CLAUSE47_SEGMENTS.map((seg) => {
                const w = target > 0 ? Math.min(((cy[seg.key] || 0) / target) * 100, 100) : 0;
                return w > 0 && (
                  <Box key={seg.key} sx={{ width: `${w}%`, bgcolor: seg.color, opacity: 0.9 }} />
                );
              })}
            </Box>
            {/* Pro-rata tick — wide + taller than the bar + haloed, so the
                expected-position mark reads at a glance. */}
            {tickPct != null && (
              <Tooltip arrow title={`Pro-rata target to date: ${fmtMWhCompact(cy.pro_rata_target_mwh)} (${cy.elapsed_pct.toFixed(0)}% of Contract Year elapsed)`}>
                <Box sx={{
                  position: 'absolute', top: -3, bottom: -3,
                  left: `calc(${tickPct}% - 2.5px)`, width: 5,
                  bgcolor: 'text.primary', borderRadius: 1, cursor: 'help',
                  boxShadow: (th) => `0 0 0 3px ${th.palette.background.paper}`,
                  zIndex: 1,
                }} />
              </Tooltip>
            )}
          </Box>

          {/* Segment legend + tracking delta */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
            {CLAUSE47_SEGMENTS.map((seg) => (
              <Stack key={seg.key} direction="row" spacing={0.5} alignItems="center">
                <FiberManualRecordIcon sx={{ fontSize: 9, color: seg.color }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {seg.label}: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {fmtMWhCompact(cy[seg.key] || 0)}
                  </Box>
                </Typography>
              </Stack>
            ))}
            <Chip
              size="small"
              label={`${ahead ? '+' : ''}${fmtMWhCompact(cy.tracking_delta_mwh)} vs pro-rata`}
              sx={{
                bgcolor: ahead ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                color: ahead ? '#22c55e' : '#f59e0b',
                height: 20, fontSize: '0.7rem', fontWeight: 700, borderRadius: '4px',
              }}
            />
          </Stack>

          {/* Outcome strip */}
          <Box sx={{
            display: 'grid', gap: 1.5,
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            pt: 1.5, borderTop: '1px dashed', borderColor: 'divider',
          }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Projected year-end ESQ
              </Typography>
              <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                color: projShortfall ? '#ef4444' : '#22c55e' }}>
                {cy.projected_esq_mwh != null ? fmtMWhCompact(cy.projected_esq_mwh) : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                at current run-rate {cy.projected_volume_mwh != null ? fmtMWhCompact(cy.projected_volume_mwh) : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Shortfall rate
              </Typography>
              <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
                {cy.shortfall_rate_aud_per_mwh != null ? fmtAUDPerMWh(cy.shortfall_rate_aud_per_mwh) : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                QLD solar VWAP {cy.qld_avg_spot_aud_per_mwh != null ? fmtAUDPerMWh(cy.qld_avg_spot_aud_per_mwh) : '—'} − {(t.fixed_price_floor_pct ?? 65).toFixed(0)}% × A${(cy.fixed_price_aud_per_mwh ?? 0).toFixed(2)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Indicative shortfall payment
              </Typography>
              <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                color: projShortfall ? '#ef4444' : 'text.primary' }}>
                {cy.projected_esq_payment_aud != null ? fmtAUDCompact(cy.projected_esq_payment_aud) : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                4.7(c) · sole remedy per 4.7(e)
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Consecutive shortfall years
              </Typography>
              <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                color: s.consecutive_shortfall_years >= 2 ? '#ef4444' : 'text.primary' }}>
                {s.consecutive_shortfall_years} / {s.termination_threshold_years}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                3 years → Buyer termination right (4.7(f))
              </Typography>
            </Box>
          </Box>

          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1.5 }}>
            Terms: Buyer&apos;s Share {t.buyers_share_pct?.toFixed(0)}% · MEG {fmtMWhCompact(t.meg_mwh_per_year)}/yr
            · MLF {t.mlf?.toFixed(4)} ({t.mlf_source}) · Fixed Price A${(cy.fixed_price_aud_per_mwh ?? 0).toFixed(2)}/MWh
            · Curtailment Strike A${(t.curtailment_strike_aud_per_mwh ?? 0).toFixed(0)}/MWh
          </Typography>
        </Box>
      )}

      {/* Completed Contract Years — appears once CY1 closes out (Jul 2026) */}
      {completed.length > 0 && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{
            display: 'grid', gap: 1, px: 2.25, py: 1,
            gridTemplateColumns: '1fr 1.5fr 1.5fr 1fr 1fr 1fr',
            bgcolor: colors.surface2,
          }}>
            {['Year', 'Volume', 'Target', 'ESQ', 'Rate', 'Payment'].map((h, i) => (
              <Typography key={h} variant="caption" sx={{
                fontWeight: 600, color: 'text.secondary', letterSpacing: '0.06em',
                textTransform: 'uppercase', textAlign: i >= 1 ? 'right' : 'left',
              }}>{h}</Typography>
            ))}
          </Box>
          {completed.map((y) => (
            <Box key={y.contract_year} sx={{
              display: 'grid', gap: 1, px: 2.25, py: 1,
              gridTemplateColumns: '1fr 1.5fr 1.5fr 1fr 1fr 1fr',
              borderTop: '1px solid', borderColor: 'divider',
            }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{y.label}</Typography>
              <Typography sx={{ fontSize: '0.85rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {fmtMWhCompact(y.adjusted_volume_mwh)}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {fmtMWhCompact(y.target_mwh)}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                color: y.esq_mwh > 0 ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
                {y.esq_mwh > 0 ? fmtMWhCompact(y.esq_mwh) : 'nil'}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {y.shortfall_rate_aud_per_mwh != null ? fmtAUDPerMWh(y.shortfall_rate_aud_per_mwh) : '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {y.esq_payment_aud != null ? fmtAUDCompact(y.esq_payment_aud) : '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  );
}


// ── Clause 4.10 — Reporting obligations board ────────────────────────────

const REPORTING_STATUS = {
  live:           { color: '#22c55e', label: 'Live' },
  partial:        { color: '#84cc16', label: 'Partial' },
  stale:          { color: '#f59e0b', label: 'Stale' },
  outstanding:    { color: '#ef4444', label: 'Outstanding' },
  not_applicable: { color: '#94a3b8', label: 'N/A' },
};

function fmtReceived(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function Clause410Card({ contract, colors }) {
  const r = contract.reporting_4_10 || {};
  const rows = r.obligations || [];
  if (!rows.length) return null;

  const met = rows.filter((o) => ['live', 'partial'].includes(o.status)).length;
  const applicable = rows.filter((o) => o.status !== 'not_applicable').length;

  return (
    <Card sx={{ mb: 2, p: 0 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between"
             sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Reporting · Clause 4.10
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {met} of {applicable} feeds flowing · Seller → Buyer obligations
        </Typography>
      </Stack>

      <Box sx={{
        display: 'grid', gap: 1, px: 2.25, py: 1,
        gridTemplateColumns: '0.9fr 3fr 1.1fr 1.3fr 1fr',
        bgcolor: colors.surface2,
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        {['Clause', 'Obligation', 'Cadence', 'Last received', 'Status'].map((h, i) => (
          <Typography key={h} variant="caption" sx={{
            fontWeight: 600, color: 'text.secondary', letterSpacing: '0.06em',
            textTransform: 'uppercase', textAlign: i === 4 ? 'right' : 'left',
          }}>{h}</Typography>
        ))}
      </Box>

      {rows.map((o) => {
        const st = REPORTING_STATUS[o.status] || REPORTING_STATUS.outstanding;
        return (
          <Box key={o.clause} sx={{
            display: 'grid', gap: 1, px: 2.25, py: 1.1, alignItems: 'center',
            gridTemplateColumns: '0.9fr 3fr 1.1fr 1.3fr 1fr',
            borderBottom: '1px solid', borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
            opacity: o.status === 'not_applicable' ? 0.55 : 1,
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {o.clause.replace('4.10(a)', '')}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: 'text.primary' }}>
                {o.obligation}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {o.detail}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {o.cadence}
            </Typography>
            <Typography variant="caption" sx={{
              color: o.last_received ? 'text.primary' : 'text.disabled',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {fmtReceived(o.last_received)}
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Chip label={st.label} size="small" sx={{
                bgcolor: `${st.color}22`, color: st.color,
                height: 18, fontSize: '0.65rem', fontWeight: 700,
                letterSpacing: '0.06em', borderRadius: '4px',
              }} />
            </Box>
          </Box>
        );
      })}
    </Card>
  );
}


// ── Page ─────────────────────────────────────────────────────────────────

export default function PpaReconciliation() {
  const { portfolioId } = useParams();
  const colors = useThemeColors();
  const [period, setPeriod] = useState(() => defaultPeriodValue('30d'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // LGC monthly settlement — independent fetch, won't block the main view.
  const [lgcMonthly, setLgcMonthly] = useState(null);
  // Clause 4.7 / 4.10 contract compliance — Contract-Year based, so it does
  // NOT refetch on period change; one fetch per portfolio.
  const [compliance, setCompliance] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Explicit start/end for every preset — the picker is the single source
    // of truth for the window (see utils/periodParams.js).
    const params = periodApiParams(period, { withFrequency: false });
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/ppa-reconcile`, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, period]);

  // LGC monthly settlement view — separate endpoint; trailing 12 months.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/lgc-settlement`,
              { params: { months: 12 } })
      .then((r) => { if (!cancelled) setLgcMonthly(r.data); })
      .catch(() => { /* hide section on failure */ });
    return () => { cancelled = true; };
  }, [portfolioId]);

  // Clause 4.7 + 4.10 contract compliance — hides its sections on failure
  // rather than blocking the period-based view below.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/ppa-contract-compliance`)
      .then((r) => { if (!cancelled) setCompliance(r.data); })
      .catch(() => { /* hide sections on failure */ });
    return () => { cancelled = true; };
  }, [portfolioId]);

  const k = data?.kpis || {};
  const captureSpread = k.avg_strike_aud_per_mwh && k.avg_spot_aud_per_mwh
    ? k.avg_strike_aud_per_mwh - k.avg_spot_aud_per_mwh
    : null;

  // No outer padding — AppLayout already supplies `py: 2`.
  return (
    <Box>
      <PpaWorkspaceTabs />

      {/* Header + period selector */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
          <Typography sx={{
            fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em',
            color: 'text.primary',
          }}>
            PPA Reconciliation
          </Typography>
          {data?.portfolio?.name && (
            <Chip label={data.portfolio.name} size="small"
                  sx={{ bgcolor: 'primary.main', color: 'background.paper',
                        fontWeight: 600, fontSize: '0.72rem', height: 22 }} />
          )}
        </Stack>
        <PeriodPicker value={period} onChange={setPeriod} size="small" />
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        ●  {data?.period?.label || '—'}
        {data?.period?.start && (
          <>  ·  {fmtDate(data.period.start)} → {fmtDate(data.period.end)}</>
        )}
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* KPI strip */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <KpiCard
          label="Settlement" loading={loading}
          value={fmtAUDCompact(k.settlement_aud || 0)}
          accent="#D98A2B"
          tooltip="Total settlement across all PPA + merchant slices in the period."
        />
        <KpiCard
          label="Min Gen vs Target" loading={loading}
          value={k.min_gen_pct_of_target != null ? `${k.min_gen_pct_of_target.toFixed(1)}%` : '—'}
          tooltip="Total delivered MWh / total contracted MWh. Targets use 35% capacity factor for PAYG; full obligation for baseload."
        />
        <KpiCard
          label="Avg Strike vs Spot" loading={loading}
          value={k.avg_strike_aud_per_mwh != null ? fmtAUDPerMWh(k.avg_strike_aud_per_mwh) : '—'}
          unit={captureSpread != null ? `· spread ${captureSpread >= 0 ? '+' : ''}${captureSpread.toFixed(0)}` : ''}
          tooltip={`Strike: ${k.avg_strike_aud_per_mwh != null ? 'A$'+k.avg_strike_aud_per_mwh.toFixed(2) : '—'}/MWh. Spot: ${k.avg_spot_aud_per_mwh != null ? 'A$'+k.avg_spot_aud_per_mwh.toFixed(2) : '—'}/MWh.`}
        />
        <KpiCard
          label="Compliance" loading={loading}
          value={k.compliance_pct != null ? `${k.compliance_pct.toFixed(0)}%` : '—'}
          tooltip="Share of PPAs delivering ≥90% of contracted MWh."
        />
      </Box>

      {/* Expected vs actual PPA revenue — the headline of this screen. Expected
          = the contract's budgeted fixed-leg revenue for the period
          (MEG × buyer's share × period-fraction × strike); actual = settled
          PPA revenue. Only shown when at least one PPA has a computable
          budget. */}
      {!loading && k.expected_revenue_aud != null && (() => {
        const exp = k.expected_revenue_aud || 0;
        const act = k.actual_revenue_aud || 0;
        const varAud = k.revenue_variance_aud || 0;
        const varPct = k.revenue_variance_pct;
        const good = varAud >= 0;                 // actual ≥ expected
        const varColor = good ? '#22c55e' : '#ef4444';
        return (
          <Card sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Expected vs actual PPA revenue
              </Typography>
              <Tooltip arrow title="Expected = contract MEG × buyer's share, prorated to the period, at the period strike. Actual = settled PPA revenue (allocated MWh × strike).">
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {data?.period?.label || ''} · budget basis
                </Typography>
              </Tooltip>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'baseline' }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Expected</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtAUDCompact(exp)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Actual</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtAUDCompact(act)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Variance</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.35rem', color: varColor, fontVariantNumeric: 'tabular-nums' }}>
                  {good ? '+' : '−'}{fmtAUDCompact(Math.abs(varAud))}
                  {varPct != null && (
                    <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, ml: 0.75 }}>
                      ({good ? '+' : ''}{varPct.toFixed(1)}%)
                    </Box>
                  )}
                </Typography>
              </Box>
            </Stack>
            {/* Expected (track) vs actual (fill) bar */}
            <Box sx={{ mt: 1.5, height: 8, borderRadius: 4, bgcolor: colors.track, overflow: 'hidden', position: 'relative' }}>
              <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${exp > 0 ? Math.min(act / exp * 100, 100) : 0}%`,
                bgcolor: varColor, opacity: 0.85, transition: 'width 240ms ease',
              }} />
            </Box>
          </Card>
        );
      })()}

      {/* Clause 4.7 Short-fall Electricity + Clause 4.10 Reporting — one
          block per PPA that carries the contract mechanics (Gunsynd today).
          Contract-Year based, independent of the period picker above. */}
      {(compliance?.contracts || []).map((c) => (
        <React.Fragment key={c.contract_id}>
          <Clause47Card contract={c} colors={colors} />
          <Clause410Card contract={c} colors={colors} />
        </React.Fragment>
      ))}

      {/* Per-PPA delivered vs contracted */}
      <Card sx={{ mb: 2, p: 0 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between"
               sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Per-PPA delivered vs contracted
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {data?.per_ppa?.length || 0} PPAs · {data?.period?.label || ''}
          </Typography>
        </Stack>
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[1,2,3].map((i) => (
              <Box key={i} sx={{ mb: 1.5 }}>
                <Skeleton variant="text" width="40%" sx={{ bgcolor: colors.skeleton }} />
                <Skeleton variant="rectangular" height={6} sx={{ bgcolor: colors.skeleton, mt: 1 }} />
              </Box>
            ))}
          </Box>
        ) : !data?.per_ppa?.length ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
            <Typography>No PPAs in this portfolio for the selected period.</Typography>
          </Box>
        ) : (
          data.per_ppa.map((p) => (
            <PerPpaRow key={p.contract_term_id} p={p} colors={colors} />
          ))
        )}
      </Card>

      {/* Two-up: Settlement composition + Shortfall attribution */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
        <Card sx={{ p: 2.25 }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Settlement composition
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              By revenue source
            </Typography>
          </Stack>
          {loading ? (
            <Box>
              {[1,2,3,4].map((i) => (
                <Skeleton key={i} variant="rectangular" height={24} sx={{ bgcolor: colors.skeleton, mb: 1 }} />
              ))}
            </Box>
          ) : !data?.settlement_composition?.length ? (
            <Typography color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>
              No settlement data in this period.
            </Typography>
          ) : (
            <Stack spacing={0}>
              {data.settlement_composition.map((row) => (
                <CompositionRow key={row.component} row={row} colors={colors} />
              ))}
            </Stack>
          )}
        </Card>

        <Card sx={{ p: 2.25 }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Shortfall attribution
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {data?.shortfall_attribution?.shortfall_mwh_total != null
                ? `Total ${fmtMWhCompact(data.shortfall_attribution.shortfall_mwh_total)}`
                : ''}
            </Typography>
          </Stack>
          {loading ? (
            <Box>
              {[1,2,3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={36} sx={{ bgcolor: colors.skeleton, mb: 1 }} />
              ))}
            </Box>
          ) : !data?.shortfall_attribution?.buckets?.length ? (
            <Typography color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>
              No shortfall in this period — all PPAs on target.
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {data.shortfall_attribution.buckets.map((b, i) => (
                <ShortfallBucket
                  key={i} bucket={b}
                  total={data.shortfall_attribution.shortfall_mwh_total}
                  colors={colors}
                />
              ))}
            </Stack>
          )}
        </Card>
      </Box>

      {/* Settlement reconciliation per counterparty */}
      <Card sx={{ p: 0 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between"
               sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Settlement reconciliation
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Per counterparty · LGCs at ${data?.lgc_ref_price_aud != null ? Number(data.lgc_ref_price_aud).toFixed(2) : '—'}/MWh ref price (warehouse spot)
          </Typography>
        </Stack>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={120} sx={{ bgcolor: colors.skeleton }} />
          </Box>
        ) : !data?.settlement_reconciliation?.length ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
            <Typography>No settlement rows.</Typography>
          </Box>
        ) : (
          <Box>
            {/* Header row */}
            <Box sx={{
              display: 'grid', gap: 1,
              gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1fr 1fr',
              px: 2, py: 1,
              borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: colors.surface2,
            }}>
              {['Counterparty','Asset','Delivered','Strike','Gross','LGC value','Status'].map((h, i) => (
                <Typography key={i} variant="caption" sx={{
                  fontWeight: 600, color: 'text.secondary',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  textAlign: i >= 2 && i <= 5 ? 'right' : 'left',
                }}>
                  {h}
                </Typography>
              ))}
            </Box>
            {data.settlement_reconciliation.map((r) => (
              <Box key={r.contract_term_id} sx={{
                display: 'grid', gap: 1,
                gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1fr 1fr',
                px: 2, py: 1.25, alignItems: 'center',
                borderBottom: '1px solid', borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.88rem' }}>
                  {r.counterparty}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {r.asset}
                </Typography>
                <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.88rem' }}>
                  {fmtMWhCompact(r.delivered_mwh)}
                </Typography>
                <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.88rem' }}>
                  {r.weighted_strike != null ? fmtAUDPerMWh(r.weighted_strike) : '—'}
                </Typography>
                <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                  fontSize: '0.88rem', fontWeight: 700, color: 'text.primary' }}>
                  {fmtAUDCompact(r.gross_aud)}
                </Typography>
                <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.88rem' }}>
                  {fmtAUDCompact(r.lgc_value_aud)}
                </Typography>
                <Box>
                  <Chip
                    label="Pending"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b',
                      fontWeight: 600, fontSize: '0.65rem', height: 18,
                      letterSpacing: '0.06em', borderRadius: '4px',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Card>

      {/* LGC monthly settlement — 12-month chart + headline. Hides itself
          when the LGC endpoint hasn't returned yet, or when the portfolio
          has no LGC-bearing generation in the window. */}
      {lgcMonthly && (lgcMonthly.totals?.settlement_aud > 0) && (
        <Card sx={{ mt: 2, p: 0 }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between"
                 sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              LGC monthly settlement
            </Typography>
            <Stack direction="row" spacing={2} alignItems="baseline">
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {lgcMonthly.totals?.lgc_volume?.toLocaleString('en-AU')} LGCs ·
                &nbsp;{fmtAUDCompact(lgcMonthly.totals?.settlement_aud)} trailing 12 mo
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                src: aus_warehouse.lgc_price (Spot)
              </Typography>
            </Stack>
          </Stack>
          <Box sx={{ px: 1, py: 1 }}>
            <BarChart
              height={220}
              dataset={lgcMonthly.months.map((m) => ({
                month:           (m.month || '').slice(0, 7),
                settlement_aud:  m.settlement_aud || 0,
                lgc_volume:      m.lgc_volume || 0,
                lgc_price:       m.lgc_aud,
              }))}
              xAxis={[{ scaleType: 'band', dataKey: 'month',
                        tickLabelStyle: { fontSize: 11 } }]}
              yAxis={[
                { id: 'aud',
                  valueFormatter: (v) => fmtAUDCompact(v),
                  tickLabelStyle: { fontSize: 11 } },
                { id: 'lgc', position: 'right',
                  valueFormatter: (v) => v == null ? '—' : `$${Number(v).toFixed(0)}`,
                  tickLabelStyle: { fontSize: 11 } },
              ]}
              series={[
                { dataKey: 'settlement_aud', label: 'Settlement (AUD)',
                  color: '#22c55e', yAxisId: 'aud',
                  valueFormatter: (v) => fmtAUDCompact(v) },
              ]}
              margin={{ left: 60, right: 50, top: 24, bottom: 32 }}
              slotProps={{
                legend: { hidden: false,
                          position: { vertical: 'top', horizontal: 'middle' } },
              }}
            />
          </Box>
        </Card>
      )}
    </Box>
  );
}
